import { Injectable } from '@angular/core';
import { NgxsOnInit, Selector, State, StateContext } from '@ngxs/store';
import { orderBy } from 'lodash';
import {
	MergedFileWithContextData,
	MortgageDocumentPackage,
} from '@models/mortgage-document-packages';
import {
	MatterPathParams,
	FirestoreStateBaseForMatter,
} from '@state/FirestoreStateBaseForMatter';
import { CollectionPaths } from '@models/collections';
import { MatterMortgagesState } from '../matter_mortgages';
import {
	DEFAULT_STATE_BASE,
	FirestoreStateBaseStateModel,
} from '@state/FirestoreStateBase';

export interface MortgagePathParams extends MatterPathParams {
	mortgageId: string;
}

export interface MortgagePackagesModel
	extends FirestoreStateBaseStateModel<MortgagePathParams> {
	items: MortgageDocumentPackage[];
}

const DEFAULT_STATE: MortgagePackagesModel = {
	...DEFAULT_STATE_BASE,
	items: [],
};
type Ctx = StateContext<MortgagePackagesModel>;

@State<MortgagePackagesModel>({
	name: 'matterMortgagePackages',
	defaults: DEFAULT_STATE,
})
@Injectable()
export class MatterMortgagePackagesState
	extends FirestoreStateBaseForMatter<
		MortgageDocumentPackage,
		MortgagePathParams,
		MortgagePackagesModel
	>
	implements NgxsOnInit
{
	/**
	 * Prevent having JS do this sorting, do it in firestore
	 * Update firestore index to sort by file.createdAt
	 * @see {@link MatterMortgagePackagesState#getQueryConstraints}
	 */
	@Selector([MatterMortgagePackagesState])
	static getAll(state: MortgagePackagesModel): MortgageDocumentPackage[] {
		if (state?.items) {
			return orderBy(state.items, 'file.createdAt', 'desc');
		}
		return [];
	}

	@Selector([MatterMortgagePackagesState.getAll])
	static getPackageFiles(packages: MortgageDocumentPackage[]): {
		[key: string]: MergedFileWithContextData;
	} {
		return packages.reduce(
			(
				acc: { [key: string]: MergedFileWithContextData },
				pack: MortgageDocumentPackage,
			) => {
				pack.file.mergedFiles.forEach((file) => {
					const fileWithPackageData = {
						...file,
						packageId: pack.id,
						fileId: pack.file.fileId,
						createdAt: pack.file.createdAt,
					};
					if (
						!acc[file.id] ||
						new Date(fileWithPackageData.createdAt) >
							new Date(acc[file.id].createdAt)
					) {
						acc[file.id] = fileWithPackageData;
					}
				});
				return acc;
			},
			{},
		);
	}

	ngxsOnInit(ctx?: Ctx): void {
		super.ngxsOnInit(ctx);
		this.store
			.select(MatterMortgagesState.getSelectedMortgageId)
			.subscribe((mortgageId) => {
				super.subscribeToCollectionChanges(ctx, {
					businessId: this.businessId,
					matterId: this.matterId,
					mortgageId,
				});
			});
	}

	constructor() {
		super(
			(pathParams) =>
				CollectionPaths.business_matter_mortgage_packages(
					pathParams.businessId,
					pathParams.matterId,
					pathParams.mortgageId,
				),
			(doc) => ({ items: doc as MortgageDocumentPackage[] }),
			() => DEFAULT_STATE,
		);
	}
}
